<template>
  <div class="portal-page info-plaza" :class="{ 'info-plaza--dark': theme === 'dark' }">
    <div class="portal-page__inner">
      <header class="info-plaza__head">
        <div class="info-plaza__head-copy">
          <h1>정보마당</h1>
          <p>왼쪽에서 폴더와 파일을 고르면 내용이 오른쪽에 열립니다.</p>
        </div>
        <div class="info-plaza__head-actions">
          <template v-if="isAdminLoggedIn">
            <button
              type="button"
              class="portal-btn info-editmode"
              :class="editMode ? 'portal-btn--primary' : 'portal-btn--secondary'"
              :aria-pressed="editMode"
              @click="toggleEditMode"
            >
              <AppIcon :icon="editMode ? 'mdi-lock-open-variant-outline' : 'mdi-pencil-outline'" :size="16" />
              {{ editMode ? '변경모드 끄기' : '변경모드' }}
            </button>
          </template>
          <button
            type="button"
            class="info-theme-toggle"
            :aria-label="theme === 'dark' ? '라이트 모드로 보기' : '다크 모드로 보기'"
            :title="theme === 'dark' ? '라이트 모드' : '다크 모드'"
            @click="toggleTheme"
          >
            <AppIcon :icon="theme === 'dark' ? 'mdi-weather-sunny' : 'mdi-weather-night'" :size="18" />
          </button>
        </div>
      </header>
      <p v-if="staffNote" class="staff-note">{{ staffNote }}</p>

      <div class="info-plaza__layout">
        <!-- 좌측: 검색 + 폴더/파일 트리 -->
        <aside
          class="info-plaza__side"
          :class="{ 'is-drop': dropTarget === 'root' }"
          aria-label="정보마당 폴더"
          @dragover="onDragOverTarget('root', $event)"
          @dragleave="onDragLeaveTarget('root')"
          @drop="onDropTarget('root')"
        >
          <div v-if="canEdit" class="info-plaza__side-actions">
            <button type="button" class="portal-btn portal-btn--secondary" @click="beginCreateFolder()">새 폴더</button>
            <button type="button" class="portal-btn portal-btn--primary" @click="triggerUpload">HTML 업로드</button>
            <input
              ref="uploadInput"
              type="file"
              accept=".html,.htm,text/html"
              multiple
              hidden
              @change="onUploadChange"
            />
          </div>
          <form class="info-plaza__search" role="search" @submit.prevent="runSearch">
            <input
              v-model.trim="keyword"
              type="search"
              class="portal-input"
              placeholder="제목·내용 검색"
              aria-label="정보마당 검색"
            />
            <button type="submit" class="portal-btn portal-btn--primary" aria-label="검색">
              <AppIcon icon="mdi-magnify" :size="18" />
            </button>
          </form>

          <div class="info-plaza__tree" @contextmenu="onContextBackground">
            <p v-if="loading" class="info-plaza__status">불러오는 중…</p>
            <p v-else-if="errorMessage" class="info-plaza__status">{{ errorMessage }}</p>

            <!-- 검색 결과: 문서 평면 목록 -->
            <template v-else-if="isSearching">
              <button type="button" class="info-tree__item info-tree__item--root" @click="selectFolder(0)">
                <AppIcon icon="mdi-arrow-left" :size="16" />
                <span class="info-tree__name">'{{ appliedKeyword }}' 검색 {{ visibleDocs.length }}건</span>
              </button>
              <div v-for="doc in visibleDocs" :key="`s-${doc.id}`" class="info-tree__row" style="--depth: 1">
                <span class="info-tree__toggle-spacer" />
                <button
                  type="button"
                  class="info-tree__item info-tree__item--doc"
                  :class="{ 'is-on': viewer?.id === doc.id, 'is-hidden': doc.status === 'HIDDEN' }"
                  @click="openDoc(doc)"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="info-tree__svg info-tree__svg--doc">
                    <path
                      d="M6 2.5h7.5L19 8v13.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 21.5v-17A1.5 1.5 0 0 1 6 2.5Z"
                      class="info-doc__svg-body"
                    />
                    <path d="M13.5 2.5V8H19L13.5 2.5Z" class="info-doc__svg-fold" />
                  </svg>
                  <span class="info-tree__name">{{ doc.title }}</span>
                  <em v-if="doc.folderName" class="info-tree__tag">{{ doc.folderName }}</em>
                </button>
              </div>
              <p v-if="!visibleDocs.length" class="info-plaza__status">검색 결과가 없습니다.</p>
            </template>

            <!-- 폴더 + 파일 트리 -->
            <template v-else>
              <button
                type="button"
                class="info-tree__item info-tree__item--root"
                :class="{ 'is-on': !selected && !viewer }"
                @click="selectFolder(0)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" class="info-tree__home">
                  <path d="M4 11.2 12 4l8 7.2V20h-5.4v-5.6H9.4V20H4v-8.8Z" fill="currentColor" />
                </svg>
                <span class="info-tree__name">전체</span>
                <em>{{ docs.length }}</em>
              </button>

              <!-- 인라인 새 폴더 -->
              <div v-if="creatingFolder" class="info-tree__row" style="--depth: 1">
                <span class="info-tree__toggle-spacer" />
                <span class="info-tree__item">
                  <svg viewBox="0 0 48 48" aria-hidden="true" class="info-tree__svg">
                    <path d="M6 13a5 5 0 0 1 5-5h8.6l4.2 5.4H37a5 5 0 0 1 5 5v2H6v-7.4Z" class="info-folder__svg-back" />
                    <path d="M6 19h36v13a5 5 0 0 1-5 5H11a5 5 0 0 1-5-5V19Z" class="info-folder__svg-front" />
                  </svg>
                  <input
                    ref="newFolderBox"
                    v-model.trim="newFolderName"
                    class="fx-rename"
                    placeholder="새 폴더"
                    aria-label="새 폴더 이름"
                    @click.stop
                    @keyup.enter="commitCreateFolder"
                    @keyup.esc="cancelCreateFolder"
                    @blur="commitCreateFolder"
                  />
                </span>
              </div>

              <template v-for="row in treeRows" :key="row.kind === 'folder' ? `f-${row.idx}` : `d-${row.doc.id}`">
                <!-- 폴더 -->
                <div
                  v-if="row.kind === 'folder'"
                  class="info-tree__row"
                  :class="{
                    'is-drop': dropTarget === row.idx,
                    'is-dragging': dragInfo?.kind === 'folder' && dragInfo.idx === row.idx,
                  }"
                  :style="{ '--depth': row.depth }"
                  :draggable="canEdit && !isRenamingId('folder', row.idx)"
                  @dragstart="onDragStart('folder', row.idx, $event)"
                  @dragend="onDragEnd"
                  @dragover="onDragOverTarget(row.idx, $event)"
                  @dragleave="onDragLeaveTarget(row.idx)"
                  @drop="onDropTarget(row.idx)"
                  @contextmenu.prevent.stop="onContextItem($event, { kind: 'folder', idx: row.idx })"
                >
                  <button
                    v-if="row.hasChildren"
                    type="button"
                    class="info-tree__toggle"
                    :class="{ 'is-open': expanded.has(row.idx) }"
                    :aria-label="expanded.has(row.idx) ? `${row.name} 접기` : `${row.name} 펼치기`"
                    @click.stop="toggleFolder(row.idx)"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
                    </svg>
                  </button>
                  <span v-else class="info-tree__toggle-spacer" />
                  <button
                    type="button"
                    class="info-tree__item"
                    :class="{ 'is-on': selected?.kind === 'folder' && selected.idx === row.idx, 'is-hidden': row.status === 'HIDDEN' }"
                    @click="onFolderClick(row.idx)"
                  >
                    <svg viewBox="0 0 48 48" aria-hidden="true" class="info-tree__svg">
                      <path d="M6 13a5 5 0 0 1 5-5h8.6l4.2 5.4H37a5 5 0 0 1 5 5v2H6v-7.4Z" class="info-folder__svg-back" />
                      <path d="M6 19h36v13a5 5 0 0 1-5 5H11a5 5 0 0 1-5-5V19Z" class="info-folder__svg-front" />
                    </svg>
                    <input
                      v-if="isRenamingId('folder', row.idx)"
                      ref="renameBox"
                      v-model.trim="renameText"
                      class="fx-rename"
                      @click.stop
                      @keyup.enter="commitRename"
                      @keyup.esc="cancelRename"
                      @blur="commitRename"
                    />
                    <template v-else>
                      <span class="info-tree__name">
                        {{ row.name }}
                        <i v-if="row.status === 'HIDDEN'" class="staff-badge">숨김</i>
                      </span>
                      <em>{{ row.count }}</em>
                    </template>
                  </button>
                </div>

                <!-- 파일(HTML) -->
                <div
                  v-else
                  class="info-tree__row"
                  :class="{ 'is-dragging': dragInfo?.kind === 'doc' && dragInfo.idx === row.doc.idx }"
                  :style="{ '--depth': row.depth }"
                  :draggable="canEdit && !!row.doc.idx && !isRenamingId('doc', row.doc.idx || 0)"
                  @dragstart="onDragStart('doc', row.doc.idx || 0, $event)"
                  @dragend="onDragEnd"
                  @contextmenu.prevent.stop="onContextItem($event, { kind: 'doc', idx: row.doc.idx || 0 })"
                >
                  <span class="info-tree__toggle-spacer" />
                  <button
                    type="button"
                    class="info-tree__item info-tree__item--doc"
                    :class="{ 'is-on': viewer?.id === row.doc.id, 'is-hidden': row.doc.status === 'HIDDEN' }"
                    @click="openDoc(row.doc)"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" class="info-tree__svg info-tree__svg--doc">
                      <path
                        d="M6 2.5h7.5L19 8v13.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 21.5v-17A1.5 1.5 0 0 1 6 2.5Z"
                        class="info-doc__svg-body"
                      />
                      <path d="M13.5 2.5V8H19L13.5 2.5Z" class="info-doc__svg-fold" />
                      <path d="M8.5 12.5h7M8.5 15.5h7M8.5 18.5h4.5" class="info-doc__svg-lines" />
                    </svg>
                    <input
                      v-if="isRenamingId('doc', row.doc.idx || 0)"
                      ref="renameBox"
                      v-model.trim="renameText"
                      class="fx-rename"
                      @click.stop
                      @keyup.enter="commitRename"
                      @keyup.esc="cancelRename"
                      @blur="commitRename"
                    />
                    <span v-else class="info-tree__name">
                      {{ row.doc.title }}
                      <i v-if="row.doc.status === 'HIDDEN'" class="staff-badge">숨김</i>
                    </span>
                  </button>
                </div>
              </template>

              <p v-if="!treeRows.length && !creatingFolder" class="info-plaza__status">폴더가 없습니다.</p>
            </template>
          </div>

          <p v-if="canEdit" class="fx-hint">
            끌어 옮기기 · 우클릭 메뉴 · <kbd>F2</kbd> 이름변경 · <kbd>Del</kbd> 삭제
          </p>
          <p v-else-if="isAdminLoggedIn" class="fx-hint">편집하려면 우측 상단 「변경모드」를 켜세요.</p>
        </aside>

        <!-- 중앙: 선택한 HTML 내용 -->
        <main class="info-plaza__reader">
          <template v-if="viewer">
            <header class="info-reader__head">
              <div class="info-reader__title">
                <h2>{{ viewer.title }}</h2>
                <span v-if="viewer.folderName" class="fx-viewer__folder">{{ viewer.folderName }}</span>
              </div>
              <div v-if="canEdit" class="fx-viewer__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  :aria-selected="viewerTab === 'view'"
                  :class="{ 'is-on': viewerTab === 'view' }"
                  @click="viewerTab = 'view'"
                >
                  보기
                </button>
                <button
                  type="button"
                  role="tab"
                  :aria-selected="viewerTab === 'edit'"
                  :class="{ 'is-on': viewerTab === 'edit' }"
                  @click="viewerTab = 'edit'"
                >
                  HTML 편집
                </button>
              </div>
            </header>

            <div class="info-reader__body">
              <iframe
                v-show="viewerTab === 'view'"
                class="info-reader__frame"
                :srcdoc="frameSrcdoc(viewer.content || '')"
                sandbox=""
                :title="`${viewer.title} 내용`"
                referrerpolicy="no-referrer"
              />
              <div v-if="viewerTab === 'edit'" class="fx-viewer__edit">
                <label class="fx-viewer__field">
                  <span>제목</span>
                  <input v-model.trim="editDraft.title" type="text" class="portal-input" />
                </label>
                <label class="fx-viewer__field fx-viewer__field--grow">
                  <span>HTML</span>
                  <textarea v-model="editDraft.content" spellcheck="false" />
                </label>
                <label class="fx-viewer__field fx-viewer__field--row">
                  <span>공개</span>
                  <select v-model="editDraft.status" class="portal-input">
                    <option value="PUBLISHED">공개</option>
                    <option value="HIDDEN">숨김</option>
                  </select>
                </label>
                <p v-if="editError" class="staff-editor__error">{{ editError }}</p>
              </div>
            </div>

            <footer v-if="canEdit && viewerTab === 'edit'" class="info-reader__foot">
              <button type="button" class="portal-btn portal-btn--secondary" @click="viewerTab = 'view'">취소</button>
              <button type="button" class="portal-btn portal-btn--primary" :disabled="savingEdit" @click="saveEdit">
                {{ savingEdit ? '저장 중…' : '저장' }}
              </button>
            </footer>
          </template>

          <section v-else class="info-cards" :aria-label="`${gridTitle} 글 목록`">
            <header class="info-cards__head">
              <h2>{{ gridTitle }}</h2>
              <span>{{ gridDocs.length }}개</span>
            </header>
            <div v-if="gridDocs.length" class="info-cards__grid">
              <button
                v-for="doc in gridDocs"
                :key="doc.id"
                type="button"
                class="info-card"
                :class="{ 'is-hidden': doc.status === 'HIDDEN' }"
                @click="openDoc(doc)"
              >
                <span class="info-card__thumb">
                  <img v-if="thumbImage(doc)" :src="thumbImage(doc)" alt="" />
                  <iframe
                    v-else-if="htmlOf(doc)"
                    class="info-card__frame"
                    :srcdoc="frameSrcdoc(htmlOf(doc))"
                    sandbox=""
                    tabindex="-1"
                    title=""
                  />
                </span>
                <span class="info-card__meta">
                  <em>{{ doc.folderName || '정보마당' }}</em>
                  <time>{{ doc.updatedAt || doc.createdAt }}</time>
                </span>
                <strong class="info-card__title">{{ doc.title }}</strong>
                <span v-if="doc.summary" class="info-card__summary">{{ doc.summary }}</span>
              </button>
            </div>
            <p v-else class="info-reader__empty">이 폴더에는 글이 없습니다.</p>
          </section>
        </main>
      </div>

      <!-- 우클릭 컨텍스트 메뉴 -->
      <template v-if="menu">
        <div class="fx-menu__veil" @click="closeMenu" @contextmenu.prevent="closeMenu" />
        <ul
          class="fx-menu"
          :style="{ top: `${menu.y}px`, left: `${menu.x}px` }"
          role="menu"
          @click.stop
          @contextmenu.prevent.stop
        >
          <template v-if="menu.target">
            <li role="none">
              <button type="button" role="menuitem" @click="menuOpen">
                {{ menu.target.kind === 'folder' ? '열기' : '보기' }}
              </button>
            </li>
            <template v-if="canEdit">
              <li v-if="menu.target.kind === 'doc'" role="none">
                <button type="button" role="menuitem" @click="menuEdit">HTML 편집</button>
              </li>
              <li class="fx-menu__sep" role="separator" />
              <li role="none">
                <button type="button" role="menuitem" @click="menuRename">이름 변경 <kbd>F2</kbd></button>
              </li>
              <li role="none">
                <button type="button" role="menuitem" @click="menuToggleStatus">{{ menuStatusLabel }}</button>
              </li>
              <li v-if="menu.target.kind === 'folder'" role="none">
                <button type="button" role="menuitem" @click="menuNewSubfolder">새 하위 폴더</button>
              </li>
              <li class="fx-menu__sep" role="separator" />
              <li role="none">
                <button type="button" role="menuitem" class="is-danger" @click="menuDelete">삭제 <kbd>Del</kbd></button>
              </li>
            </template>
          </template>
          <template v-else-if="canEdit">
            <li role="none">
              <button type="button" role="menuitem" @click="beginCreateFolder()">새 폴더</button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" @click="triggerUpload">HTML 업로드</button>
            </li>
            <li class="fx-menu__sep" role="separator" />
            <li role="none">
              <button type="button" role="menuitem" @click="refreshList">새로고침</button>
            </li>
          </template>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useInfoTheme } from '@/composables/useInfoTheme'
import { confirmDialog } from '@/composables/useConfirmDialog'
import {
  fetchAdminInfo,
  fetchPublicInfo,
  fetchPublicInfoDoc,
  moveInfoDoc,
  moveInfoFolder,
  removeInfoDoc,
  removeInfoFolder,
  saveInfoDoc,
  saveInfoFolder,
  type InfoDoc,
  type InfoFolder,
  type InfoStatus,
} from '@/api/infoPlaza'

type Selection = { kind: 'folder' | 'doc'; idx: number }
type TreeRow =
  | { kind: 'folder'; idx: number; name: string; status: InfoStatus; depth: number; hasChildren: boolean; count: number }
  | { kind: 'doc'; doc: InfoDoc; depth: number }

const route = useRoute()
const router = useRouter()
const { isAdminLoggedIn, initAdminAuth } = useAdminAuth()
const { theme, toggleTheme } = useInfoTheme()

/* 변경모드 — 관리자가 켰을 때만 편집 가능 */
const editMode = ref(false)
const canEdit = computed(() => isAdminLoggedIn.value && editMode.value)

const folders = ref<InfoFolder[]>([])
const docs = ref<InfoDoc[]>([])
const keyword = ref('')
const appliedKeyword = ref('')
const loading = ref(false)
const errorMessage = ref('')
const staffNote = ref('')
const expanded = ref<Set<number>>(new Set())

const creatingFolder = ref(false)
const newFolderName = ref('')
const newFolderBox = ref<HTMLInputElement | null>(null)
const createParent = ref<number | null>(null)

const menu = ref<{ x: number; y: number; target: Selection | null } | null>(null)

const selected = ref<Selection | null>(null)
const renaming = ref<Selection | null>(null)
const renameText = ref('')
const renameBox = ref<HTMLInputElement[] | HTMLInputElement | null>(null)

const uploadInput = ref<HTMLInputElement | null>(null)

const viewer = ref<InfoDoc | null>(null)
const viewerTab = ref<'view' | 'edit'>('view')
const editDraft = ref<{ title: string; content: string; status: InfoStatus }>({
  title: '',
  content: '',
  status: 'PUBLISHED',
})
const savingEdit = ref(false)
const editError = ref('')

const dragInfo = ref<Selection | null>(null)
const dropTarget = ref<number | 'root' | null>(null)

const activeFolder = computed(() => {
  const value = Number(route.query.folder)
  return Number.isFinite(value) && value > 0 ? value : 0
})

const isSearching = computed(() => Boolean(appliedKeyword.value))

const childrenOf = computed(() => {
  const map = new Map<number | null, InfoFolder[]>()
  for (const folder of folders.value) {
    const key = folder.parentIdx ?? null
    const list = map.get(key)
    if (list) list.push(folder)
    else map.set(key, [folder])
  }
  for (const list of map.values()) list.sort((a, b) => a.sort - b.sort || a.idx - b.idx)
  return map
})

const docsByFolder = computed(() => {
  const map = new Map<number, InfoDoc[]>()
  for (const doc of docs.value) {
    const list = map.get(doc.folderIdx)
    if (list) list.push(doc)
    else map.set(doc.folderIdx, [doc])
  }
  for (const list of map.values())
    list.sort((a, b) => a.sort - b.sort || b.createdAt.localeCompare(a.createdAt))
  return map
})

function descendantFolderIds(idx: number): number[] {
  const children = folders.value.filter((folder) => folder.parentIdx === idx)
  return [idx, ...children.flatMap((folder) => descendantFolderIds(folder.idx))]
}

function countInFolder(idx: number) {
  const ids = new Set(descendantFolderIds(idx))
  return docs.value.filter((doc) => ids.has(doc.folderIdx)).length
}

/* 폴더 + (펼쳐지면) 하위 폴더·파일을 한 줄씩 평면화 */
const treeRows = computed<TreeRow[]>(() => {
  const rows: TreeRow[] = []
  const walk = (parent: number | null, depth: number) => {
    for (const folder of childrenOf.value.get(parent) ?? []) {
      const subfolders = childrenOf.value.get(folder.idx) ?? []
      const docsHere = docsByFolder.value.get(folder.idx) ?? []
      rows.push({
        kind: 'folder',
        idx: folder.idx,
        name: folder.name,
        status: folder.status,
        depth,
        hasChildren: subfolders.length > 0 || docsHere.length > 0,
        count: countInFolder(folder.idx),
      })
      if (expanded.value.has(folder.idx)) {
        walk(folder.idx, depth + 1)
        for (const doc of docsHere) rows.push({ kind: 'doc', doc, depth: depth + 1 })
      }
    }
  }
  walk(null, 0)
  return rows
})

/* 검색 결과(문서 평면 목록) */
const visibleDocs = computed(() =>
  [...docs.value].sort((a, b) => a.sort - b.sort || b.createdAt.localeCompare(a.createdAt)),
)

/* 가운데 썸네일 그리드: 전체는 모든 글, 폴더는 그 폴더와 하위 글 */
const gridDocs = computed(() => {
  if (isSearching.value) return visibleDocs.value
  const folderIdx = selected.value?.kind === 'folder' ? selected.value.idx : 0
  if (!folderIdx) return visibleDocs.value
  const ids = new Set(descendantFolderIds(folderIdx))
  return visibleDocs.value.filter((doc) => ids.has(doc.folderIdx))
})

const gridTitle = computed(() => {
  if (isSearching.value) return `'${appliedKeyword.value}' 검색`
  if (selected.value?.kind === 'folder') {
    return folders.value.find((folder) => folder.idx === selected.value?.idx)?.name || '폴더'
  }
  return '전체'
})

const htmlCache = ref<Record<string, string>>({})

function htmlOf(doc: InfoDoc) {
  return doc.content ?? htmlCache.value[doc.id] ?? ''
}

function thumbImage(doc: InfoDoc) {
  const html = htmlOf(doc)
  const match = html.match(/<img\b[^>]*\ssrc=["']([^"']+)["']/i)
  const src = match?.[1]?.trim() || ''
  if (!src || /^javascript:/i.test(src) || src.startsWith('data:text')) return ''
  return src
}

let thumbToken = 0
watch(gridDocs, (list) => {
  const token = ++thumbToken
  const missing = list.filter((doc) => doc.content == null && htmlCache.value[doc.id] == null)
  if (!missing.length) return
  void Promise.all(
    missing.map(async (doc) => {
      try {
        const full = await fetchPublicInfoDoc(doc.id)
        if (token !== thumbToken) return
        htmlCache.value = { ...htmlCache.value, [doc.id]: full.content || '' }
      } catch {
        /* 썸네일만 비운다 */
      }
    }),
  )
})

function toggleEditMode() {
  editMode.value = !editMode.value
}

/* 변경모드를 끄면 진행 중이던 편집 UI를 모두 정리한다 */
watch(editMode, (on) => {
  if (on) return
  cancelRename()
  cancelCreateFolder()
  closeMenu()
  if (viewerTab.value === 'edit') viewerTab.value = 'view'
})

function toggleFolder(idx: number) {
  const next = new Set(expanded.value)
  if (next.has(idx)) next.delete(idx)
  else next.add(idx)
  expanded.value = next
}

function expandAncestors(idx: number) {
  const next = new Set(expanded.value)
  let current = folders.value.find((folder) => folder.idx === idx)
  let guard = 0
  while (current && guard < 20) {
    next.add(current.idx)
    guard += 1
    current = folders.value.find((folder) => folder.idx === current?.parentIdx)
  }
  expanded.value = next
}

async function setFolderQuery(idx: number) {
  const query = { ...route.query }
  if (idx) query.folder = String(idx)
  else delete query.folder
  await router.replace({ path: '/info/library', query }).catch(() => {})
}

async function clearSearchIfAny() {
  if (!appliedKeyword.value) return
  keyword.value = ''
  appliedKeyword.value = ''
  await load({ silent: true })
}

/* "전체" 루트 또는 메뉴에서 폴더 선택 — 가운데는 썸네일 그리드 */
async function selectFolder(idx: number) {
  viewer.value = null
  selected.value = idx ? { kind: 'folder', idx } : null
  await setFolderQuery(idx)
  if (idx) expandAncestors(idx)
  await clearSearchIfAny()
}

/* 트리에서 폴더 이름 클릭: 이미 열린 폴더는 유지한 채 이 폴더만 펼친다 */
async function onFolderClick(idx: number) {
  viewer.value = null
  const next = new Set(expanded.value)
  next.add(idx)
  expanded.value = next
  selected.value = { kind: 'folder', idx }
  await clearSearchIfAny()
  await setFolderQuery(idx)
}

async function runSearch() {
  viewer.value = null
  appliedKeyword.value = keyword.value.trim()
  selected.value = null
  await load()
}

async function load(opts: { silent?: boolean } = {}) {
  if (!opts.silent) loading.value = true
  errorMessage.value = ''
  try {
    await initAdminAuth()
    const data = isAdminLoggedIn.value
      ? await fetchAdminInfo(appliedKeyword.value)
      : await fetchPublicInfo({ keyword: appliedKeyword.value })
    folders.value = data.folders
    docs.value = data.docs
    const open = new Set(expanded.value)
    for (const folder of data.folders) {
      if (data.folders.some((item) => item.parentIdx === folder.idx)) open.add(folder.idx)
    }
    expanded.value = open
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '정보마당을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

/* ---------- 인라인 새 폴더 ---------- */
async function beginCreateFolder(parentOverride?: number | null) {
  closeMenu()
  const parent = parentOverride !== undefined ? parentOverride : activeFolder.value || null
  if (isSearching.value) await selectFolder(parent || 0)
  if (parent) expandAncestors(parent)
  createParent.value = parent
  newFolderName.value = '새 폴더'
  creatingFolder.value = true
  await nextTick()
  newFolderBox.value?.focus()
  newFolderBox.value?.select()
}

function cancelCreateFolder() {
  creatingFolder.value = false
  newFolderName.value = ''
}

async function commitCreateFolder() {
  if (!creatingFolder.value) return
  const name = newFolderName.value.trim()
  creatingFolder.value = false
  if (!name) {
    newFolderName.value = ''
    return
  }
  try {
    const parentIdx = createParent.value
    const siblings = childrenOf.value.get(parentIdx) ?? []
    const sort = siblings.reduce((max, f) => Math.max(max, f.sort), 0) + 1
    await saveInfoFolder({ name, parentIdx, status: 'PUBLISHED', sort })
    staffNote.value = '폴더를 만들었습니다.'
    await load({ silent: true })
  } catch (error) {
    staffNote.value = error instanceof Error ? error.message : '폴더 생성에 실패했습니다.'
  } finally {
    newFolderName.value = ''
  }
}

/* ---------- 삭제 ---------- */
async function removeSelected() {
  if (!selected.value) return
  if (selected.value.kind === 'folder') {
    const folder = folders.value.find((f) => f.idx === selected.value?.idx)
    if (folder) await removeFolderItem(folder)
  } else {
    const doc = docs.value.find((d) => d.idx === selected.value?.idx)
    if (doc) await removeDoc(doc)
  }
}

async function removeDoc(doc: InfoDoc) {
  if (!doc.idx) return
  const ok = await confirmDialog({
    title: '파일 삭제',
    message: '이 HTML 파일을 삭제할까요? 삭제 후 복구할 수 없습니다.',
    detail: doc.title,
    confirmLabel: '삭제',
    tone: 'danger',
  })
  if (!ok) return
  try {
    await removeInfoDoc(doc.idx)
    if (viewer.value?.idx === doc.idx) viewer.value = null
    selected.value = null
    staffNote.value = '파일을 삭제했습니다.'
    await load({ silent: true })
  } catch (error) {
    staffNote.value = error instanceof Error ? error.message : '삭제에 실패했습니다.'
  }
}

async function removeFolderItem(folder: InfoFolder) {
  const ok = await confirmDialog({
    title: '폴더 삭제',
    message: '이 폴더를 삭제할까요? 폴더 안에 파일이나 하위 폴더가 있으면 삭제되지 않습니다.',
    detail: folder.name,
    confirmLabel: '삭제',
    tone: 'danger',
  })
  if (!ok) return
  try {
    await removeInfoFolder(folder.idx)
    selected.value = null
    staffNote.value = '폴더를 삭제했습니다.'
    await load({ silent: true })
  } catch (error) {
    staffNote.value = error instanceof Error ? error.message : '폴더 삭제에 실패했습니다.'
  }
}

/* ---------- 이름 변경(인라인) ---------- */
function isRenamingId(kind: 'folder' | 'doc', idx: number) {
  return renaming.value?.kind === kind && renaming.value.idx === idx
}

async function startRename(sel: Selection) {
  if (sel.kind === 'folder') {
    renameText.value = folders.value.find((f) => f.idx === sel.idx)?.name || ''
  } else {
    renameText.value = docs.value.find((d) => d.idx === sel.idx)?.title || ''
  }
  renaming.value = { ...sel }
  await nextTick()
  const box = Array.isArray(renameBox.value) ? renameBox.value[0] : renameBox.value
  box?.focus()
  box?.select()
}

function cancelRename() {
  renaming.value = null
  renameText.value = ''
}

async function commitRename() {
  const target = renaming.value
  if (!target) return
  const name = renameText.value.trim()
  renaming.value = null
  if (!name) return
  try {
    if (target.kind === 'folder') {
      const folder = folders.value.find((f) => f.idx === target.idx)
      if (!folder || folder.name === name) return
      await saveInfoFolder({ name, parentIdx: folder.parentIdx, status: folder.status, sort: folder.sort }, folder.idx)
      staffNote.value = '폴더 이름을 바꿨습니다.'
    } else {
      const doc = docs.value.find((d) => d.idx === target.idx)
      if (!doc || !doc.idx || doc.title === name) return
      await saveInfoDoc(
        {
          title: name,
          folderIdx: doc.folderIdx,
          content: doc.content ?? '',
          summary: doc.summary,
          status: doc.status,
          sort: doc.sort,
        },
        doc.idx,
      )
      if (viewer.value?.idx === doc.idx) viewer.value = { ...viewer.value, title: name }
      staffNote.value = '파일 이름을 바꿨습니다.'
    }
    await load({ silent: true })
  } catch (error) {
    staffNote.value = error instanceof Error ? error.message : '이름 변경에 실패했습니다.'
  }
}

/* ---------- HTML 업로드 ---------- */
function triggerUpload() {
  closeMenu()
  if (!folders.value.length) {
    staffNote.value = 'HTML을 올리려면 폴더가 먼저 필요합니다. 「새 폴더」로 만들어 주세요.'
    return
  }
  uploadInput.value?.click()
}

function readFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error(`${file.name} 을(를) 읽지 못했습니다.`))
    reader.readAsText(file, 'utf-8')
  })
}

async function onUploadChange(event: Event) {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files || [])
  input.value = ''
  if (!files.length) return
  const folderIdx = activeFolder.value || folders.value[0]?.idx
  if (!folderIdx) {
    staffNote.value = 'HTML을 올릴 폴더를 먼저 선택해 주세요.'
    return
  }
  let done = 0
  for (const file of files) {
    const isHtml = /\.(html?|htm)$/i.test(file.name) || file.type === 'text/html'
    if (!isHtml) {
      staffNote.value = 'HTML 파일(.html)만 올릴 수 있습니다.'
      continue
    }
    try {
      const content = await readFile(file)
      const title = file.name.replace(/\.(html?|htm)$/i, '').trim() || 'HTML 문서'
      await saveInfoDoc({ title, folderIdx, content, status: 'PUBLISHED' })
      done += 1
    } catch (error) {
      staffNote.value = error instanceof Error ? error.message : '업로드에 실패했습니다.'
    }
  }
  if (done) {
    expandAncestors(folderIdx)
    staffNote.value = `HTML ${done}개를 올렸습니다.`
    await load({ silent: true })
  }
}

/* ---------- 파일 열기(중앙 리더) ---------- */
async function openDoc(doc: InfoDoc) {
  selected.value = doc.idx ? { kind: 'doc', idx: doc.idx } : null
  viewerTab.value = 'view'
  editError.value = ''
  let content = doc.content
  if (content == null) {
    try {
      const full = await fetchPublicInfoDoc(doc.id)
      content = full.content ?? ''
    } catch {
      content = ''
    }
  }
  const resolved: InfoDoc = { ...doc, content: content ?? '' }
  viewer.value = resolved
  editDraft.value = {
    title: resolved.title,
    content: resolved.content ?? '',
    status: resolved.status === 'HIDDEN' ? 'HIDDEN' : 'PUBLISHED',
  }
}

function frameSrcdoc(html: string) {
  const content = html || ''
  if (/<\s*(!doctype|html)\b/i.test(content)) return content
  return `<!DOCTYPE html><html lang="ko"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>:root{color-scheme:light}body{margin:0;padding:24px;font-family:'Noto Sans KR','Apple SD Gothic Neo',system-ui,sans-serif;line-height:1.85;color:#1b1f27;word-break:keep-all}img{max-width:100%;height:auto}table{border-collapse:collapse}</style></head><body>${content}</body></html>`
}

async function saveEdit() {
  const current = viewer.value
  if (!current?.idx || savingEdit.value) return
  const title = editDraft.value.title.trim()
  const content = editDraft.value.content
  if (!title) {
    editError.value = '제목을 입력해 주세요.'
    return
  }
  if (!content.trim()) {
    editError.value = 'HTML 내용을 입력해 주세요.'
    return
  }
  savingEdit.value = true
  editError.value = ''
  try {
    const saved = await saveInfoDoc(
      { title, folderIdx: current.folderIdx, content, status: editDraft.value.status, sort: current.sort },
      current.idx,
    )
    viewer.value = { ...saved, content }
    editDraft.value.title = saved.title
    editDraft.value.status = saved.status === 'HIDDEN' ? 'HIDDEN' : 'PUBLISHED'
    viewerTab.value = 'view'
    staffNote.value = '파일을 저장했습니다.'
    await load({ silent: true })
  } catch (error) {
    editError.value = error instanceof Error ? error.message : '저장에 실패했습니다.'
  } finally {
    savingEdit.value = false
  }
}

/* ---------- 드래그 이동 ---------- */
function onDragStart(kind: 'folder' | 'doc', idx: number, event: DragEvent) {
  if (!canEdit.value || !idx) return
  closeMenu()
  selected.value = { kind, idx }
  dragInfo.value = { kind, idx }
  document.body.classList.add('fx-is-dragging')
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', `${kind}:${idx}`)
  }
}

function onDragEnd() {
  dragInfo.value = null
  dropTarget.value = null
  document.body.classList.remove('fx-is-dragging')
}

function canDropOn(target: number | 'root'): boolean {
  const d = dragInfo.value
  if (!d) return false
  if (target === 'root') return d.kind === 'folder'
  if (d.kind === 'doc') return true
  if (d.idx === target) return false
  return !descendantFolderIds(d.idx).includes(target)
}

function onDragOverTarget(target: number | 'root', event: DragEvent) {
  if (!dragInfo.value || !canDropOn(target)) return
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  dropTarget.value = target
}

function onDragLeaveTarget(target: number | 'root') {
  if (dropTarget.value === target) dropTarget.value = null
}

async function onDropTarget(target: number | 'root') {
  const d = dragInfo.value
  dropTarget.value = null
  dragInfo.value = null
  if (!d || !canDropOn(target)) return
  try {
    if (d.kind === 'doc') {
      if (target === 'root') {
        staffNote.value = '파일은 폴더 안으로만 옮길 수 있습니다.'
        return
      }
      const doc = docs.value.find((item) => item.idx === d.idx)
      if (doc?.folderIdx === target) return
      await moveInfoDoc(d.idx, target)
      expandAncestors(target)
      staffNote.value = '파일을 옮겼습니다.'
    } else {
      const parentIdx = target === 'root' ? null : target
      const folder = folders.value.find((item) => item.idx === d.idx)
      if ((folder?.parentIdx ?? null) === parentIdx) return
      await moveInfoFolder(d.idx, parentIdx)
      if (parentIdx) expandAncestors(parentIdx)
      staffNote.value = '폴더를 옮겼습니다.'
    }
    await load({ silent: true })
  } catch (error) {
    staffNote.value = error instanceof Error ? error.message : '이동에 실패했습니다.'
  }
}

/* ---------- 우클릭 컨텍스트 메뉴 ---------- */
const menuStatusLabel = computed(() => {
  const t = menu.value?.target
  if (!t) return ''
  const item =
    t.kind === 'folder'
      ? folders.value.find((f) => f.idx === t.idx)
      : docs.value.find((d) => d.idx === t.idx)
  return item?.status === 'HIDDEN' ? '공개로 전환' : '숨김으로 전환'
})

function openMenuAt(event: MouseEvent, target: Selection | null) {
  if (target) selected.value = { ...target }
  else selected.value = null
  const menuWidth = 220
  const rows = target ? (canEdit.value ? 6 : 1) : 4
  const menuHeight = rows * 40 + 16
  const pad = 8
  const x = Math.min(event.clientX, window.innerWidth - menuWidth - pad)
  const y = Math.min(event.clientY, window.innerHeight - menuHeight - pad)
  menu.value = { x: Math.max(pad, x), y: Math.max(pad, y), target }
}

function onContextItem(event: MouseEvent, target: Selection) {
  if (!target.idx) return
  openMenuAt(event, target)
}

function onContextBackground(event: MouseEvent) {
  if (!canEdit.value) return
  event.preventDefault()
  openMenuAt(event, null)
}

function closeMenu() {
  menu.value = null
}

function menuOpen() {
  const t = menu.value?.target
  closeMenu()
  if (!t) return
  if (t.kind === 'folder') void onFolderClick(t.idx)
  else {
    const doc = docs.value.find((d) => d.idx === t.idx)
    if (doc) void openDoc(doc)
  }
}

function menuRename() {
  const t = menu.value?.target
  closeMenu()
  if (t) void startRename(t)
}

function menuDelete() {
  const t = menu.value?.target
  closeMenu()
  if (!t) return
  selected.value = { ...t }
  void removeSelected()
}

async function menuEdit() {
  const t = menu.value?.target
  closeMenu()
  if (!t || t.kind !== 'doc') return
  const doc = docs.value.find((d) => d.idx === t.idx)
  if (!doc) return
  await openDoc(doc)
  viewerTab.value = 'edit'
}

async function menuNewSubfolder() {
  const t = menu.value?.target
  closeMenu()
  if (!t || t.kind !== 'folder') return
  await selectFolder(t.idx)
  await beginCreateFolder(t.idx)
}

async function menuToggleStatus() {
  const t = menu.value?.target
  closeMenu()
  if (!t) return
  try {
    if (t.kind === 'folder') {
      const folder = folders.value.find((f) => f.idx === t.idx)
      if (!folder) return
      const status: InfoStatus = folder.status === 'HIDDEN' ? 'PUBLISHED' : 'HIDDEN'
      await saveInfoFolder({ name: folder.name, parentIdx: folder.parentIdx, status, sort: folder.sort }, folder.idx)
    } else {
      const doc = docs.value.find((d) => d.idx === t.idx)
      if (!doc || !doc.idx) return
      const status: InfoStatus = doc.status === 'HIDDEN' ? 'PUBLISHED' : 'HIDDEN'
      let content = doc.content
      if (content == null) {
        const full = await fetchPublicInfoDoc(doc.id)
        content = full.content ?? ''
      }
      await saveInfoDoc(
        { title: doc.title, folderIdx: doc.folderIdx, content, summary: doc.summary, status, sort: doc.sort },
        doc.idx,
      )
    }
    staffNote.value = '공개 상태를 바꿨습니다.'
    await load({ silent: true })
  } catch (error) {
    staffNote.value = error instanceof Error ? error.message : '상태 변경에 실패했습니다.'
  }
}

async function refreshList() {
  closeMenu()
  await load({ silent: true })
  staffNote.value = '목록을 새로고침했습니다.'
}

/* ---------- 키보드 단축키 ---------- */
const flatItems = computed<Selection[]>(() => {
  if (isSearching.value)
    return visibleDocs.value.filter((d) => d.idx).map((d) => ({ kind: 'doc' as const, idx: d.idx as number }))
  return treeRows.value
    .map((r): Selection => (r.kind === 'folder' ? { kind: 'folder', idx: r.idx } : { kind: 'doc', idx: r.doc.idx || 0 }))
    .filter((s) => s.idx)
})

function openSelected() {
  const s = selected.value
  if (!s) return
  if (s.kind === 'folder') void onFolderClick(s.idx)
  else {
    const doc = docs.value.find((d) => d.idx === s.idx)
    if (doc) void openDoc(doc)
  }
}

function goUp() {
  if (isSearching.value || !activeFolder.value) return
  const current = folders.value.find((f) => f.idx === activeFolder.value)
  void selectFolder(current?.parentIdx || 0)
}

function onKeydown(event: KeyboardEvent) {
  if (menu.value) {
    if (event.key === 'Escape') closeMenu()
    return
  }
  if (creatingFolder.value || renaming.value) return
  const tag = (event.target as HTMLElement | null)?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    const items = flatItems.value
    if (!items.length) return
    event.preventDefault()
    let i = items.findIndex((it) => it.kind === selected.value?.kind && it.idx === selected.value?.idx)
    if (event.key === 'ArrowDown') i = i < 0 ? 0 : Math.min(items.length - 1, i + 1)
    else i = i < 0 ? items.length - 1 : Math.max(0, i - 1)
    selected.value = { ...items[i] }
    return
  }

  if (event.key === 'Backspace') {
    event.preventDefault()
    goUp()
    return
  }
  if (event.key === 'Escape') {
    selected.value = null
    return
  }
  if (!selected.value) return

  if (event.key === 'Enter') {
    event.preventDefault()
    openSelected()
  } else if (event.key === 'F2' && canEdit.value) {
    event.preventDefault()
    void startRename(selected.value)
  } else if (event.key === 'Delete' && canEdit.value) {
    event.preventDefault()
    void removeSelected()
  }
}

onMounted(async () => {
  await load()
  if (activeFolder.value) {
    expandAncestors(activeFolder.value)
    selected.value = { kind: 'folder', idx: activeFolder.value }
  }
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', closeMenu)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', closeMenu)
  document.body.classList.remove('fx-is-dragging')
})
</script>

<style lang="scss" src="./InfoPlazaPage.scss"></style>
<style lang="scss" src="../../components/portal/staffEditor.scss"></style>
